import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, Search, Users } from 'lucide-react';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'office_staff', label: 'Office Staff' },
  { value: 'receptionist', label: 'Receptionist' },
  { value: 'trader', label: 'Trader' },
  { value: 'auditor', label: 'Auditor (read-only)' },
];

const emptyForm = { email: '', password: '', name: '', role: 'trader', branch_id: '', phone: '', status: 'active', username: '', joining_date: new Date().toISOString().split('T')[0], salary: '', internal_notes: '' };

export default function EmployeesPage() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchData = async () => {
    try {
      const [uRes, bRes] = await Promise.all([api.get('/users'), api.get('/branches').catch(() => ({ data: [] }))]);
      setUsers(uRes.data.filter(u => u.role !== 'investor'));
      setBranches(bRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm({...emptyForm}); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: '', name: u.name, role: u.role, branch_id: u.branch_id || '', phone: u.phone || '', status: u.status, username: u.username || '', joining_date: u.joining_date || '', salary: u.salary || '', internal_notes: u.internal_notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {...form};
      if (!payload.password && editing) delete payload.password;
      if (!payload.password && !editing) payload.password = process.env.REACT_APP_DEFAULT_USER_PASSWORD || 'Password@123';
      if (!payload.username) payload.username = payload.email.split('@')[0];
      if (editing) { await api.put(`/users/${editing.id}`, payload); toast.success('Employee updated'); }
      else { await api.post('/users', payload); toast.success('Employee created'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    try { await api.delete(`/users/${id}`); toast.success('Employee deleted'); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const getBranchName = (id) => branches.find(b => b.id === id)?.name || '-';
  const getRoleLabel = (r) => ROLES.find(rl => rl.value === r)?.label || r;
  const filtered = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const roleCounts = ROLES.reduce((acc, r) => { acc[r.value] = users.filter(u => u.role === r.value).length; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Employees</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Manage all team members and their roles</p>
        </div>
        <Button onClick={openNew} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="add-employee-btn">
          <Plus size={16} className="mr-2" /> Add Employee
        </Button>
      </div>

      {/* Role Stats */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={roleFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setRoleFilter('all')}
          className={`rounded-sm text-xs ${roleFilter === 'all' ? 'bg-brand-primary text-white' : 'border-brand-border'}`} data-testid="filter-all">
          All ({users.length})
        </Button>
        {ROLES.map(r => (
          <Button key={r.value} variant={roleFilter === r.value ? 'default' : 'outline'} size="sm" onClick={() => setRoleFilter(r.value)}
            className={`rounded-sm text-xs ${roleFilter === r.value ? 'bg-brand-primary text-white' : 'border-brand-border'}`} data-testid={`filter-${r.value}`}>
            {r.label} ({roleCounts[r.value] || 0})
          </Button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees..." className="pl-9 bg-white border-brand-border rounded-sm" data-testid="employee-search" />
      </div>

      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="employees-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Name', 'Email', 'Username', 'Role', 'Branch', 'Phone', 'Joining', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={9} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={9} className="py-12 text-center text-brand-text-secondary">No employees found</td></tr> :
                 filtered.map(u => (
                  <tr key={u.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 font-medium text-brand-text-primary">{u.name}</td>
                    <td className="py-3 px-4 text-brand-text-secondary text-xs">{u.email}</td>
                    <td className="py-3 px-4 text-brand-text-secondary text-xs font-mono">{u.username || '-'}</td>
                    <td className="py-3 px-4"><Badge className="text-xs border-0 bg-brand-primary/10 text-brand-primary">{getRoleLabel(u.role)}</Badge></td>
                    <td className="py-3 px-4 text-brand-text-secondary">{getBranchName(u.branch_id)}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{u.phone || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary text-xs">{u.joining_date || '-'}</td>
                    <td className="py-3 px-4"><Badge className={`text-xs border-0 ${u.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{u.status}</Badge></td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} className="h-8 w-8" data-testid={`edit-emp-${u.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} className="h-8 w-8 text-brand-error" data-testid={`delete-emp-${u.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? 'Edit Employee' : 'New Employee'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2"><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="emp-name" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="emp-email" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Username / Login ID</Label><Input value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="Auto-generated from email" data-testid="emp-username" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Password {editing ? '(leave blank to keep)' : '(default: Password@123)'}</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="emp-password" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="emp-phone" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="emp-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Branch</Label>
              <Select value={form.branch_id} onValueChange={v => setForm({...form, branch_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="emp-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Joining Date</Label><Input type="date" value={form.joining_date} onChange={e => setForm({...form, joining_date: e.target.value})} data-testid="emp-joining" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="emp-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Salary / Notes</Label><Input value={form.salary} onChange={e => setForm({...form, salary: e.target.value})} placeholder="Optional" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="sm:col-span-2 space-y-2"><Label>Internal Notes</Label><Textarea value={form.internal_notes} onChange={e => setForm({...form, internal_notes: e.target.value})} placeholder="Private notes about this employee..." className="bg-white border-brand-border rounded-sm" data-testid="emp-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="emp-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
